'use es6';

import { fromJS, List } from 'immutable';
import { handleActions } from 'redux-actions';
import actionTypes from '../constants/actionTypes';
import * as FILTERS from '../constants/reportFilters';
import requestStatusTypes from '../constants/requestStatusTypes';
const {
  GET_SAVED_REPORTS_REQUESTED,
  GET_SAVED_REPORTS_SUCCEEDED,
  GET_SAVED_REPORTS_FAILED,
  GET_REPORT_TEMPLATE_SEARCH_REQUESTED,
  GET_REPORT_TEMPLATE_SEARCH_SUCCEEDED,
  GET_REPORT_TEMPLATE_SEARCH_FAILED,
  GET_ALL_RESTORABLE_REPORTS_REQUESTED,
  GET_ALL_RESTORABLE_REPORTS_SUCCEEDED,
  GET_ALL_RESTORABLE_REPORTS_FAILED,
  GET_REPORT_REQUESTED,
  GET_REPORT_SUCCEEDED,
  GET_REPORT_FAILED,
  CLEAR_REPORT,
  UPDATE_REPORT_REQUESTED,
  UPDATE_REPORT_SUCCEEDED,
  UPDATE_REPORT_FAILED,
  GET_REPORT_COUNTS_REQUESTED,
  GET_REPORT_COUNTS_SUCCEEDED,
  GET_REPORT_COUNTS_FAILED,
  GET_FILTERED_REPORT_COUNTS_REQUESTED,
  GET_FILTERED_REPORT_COUNTS_SUCCEEDED,
  GET_FILTERED_REPORT_COUNTS_FAILED,
  SET_FILTERS,
  CLEAR_FILTERS,
  SET_TEMPLATES_LIMIT,
  GET_AI_SEARCH_REQUESTED,
  GET_AI_SEARCH_SUCCEEDED,
  GET_AI_SEARCH_FAILED
} = actionTypes;
const {
  UNINITIALIZED,
  PENDING,
  SUCCEEDED,
  FAILED
} = requestStatusTypes;
const replaceOrAddReport = (reports, report) => {
  const reportIndex = reports.findIndex(r => r.get('id') === report.get('id'));
  return reportIndex > -1 ? reports.set(reportIndex, report) : reports.push(report);
};
export const initialState = fromJS({
  status: {
    getSavedReports: UNINITIALIZED,
    getReportTemplateSearchResults: UNINITIALIZED,
    normalizeReports: UNINITIALIZED,
    getReport: UNINITIALIZED,
    getReportCounts: UNINITIALIZED,
    getFilteredReportCount: UNINITIALIZED,
    createCustomReport: UNINITIALIZED,
    updateCustomReport: UNINITIALIZED,
    deleteReport: UNINITIALIZED,
    getRestorableReports: UNINITIALIZED,
    getAiSearch: UNINITIALIZED
  },
  reports: {
    offset: undefined,
    limit: undefined,
    reports: List(),
    report: undefined
  },
  report: undefined,
  reportCounts: {
    allReports: undefined,
    customReports: undefined
  },
  filters: {
    selectAll: false,
    offset: 0,
    limit: 15,
    [FILTERS.SEARCH]: undefined,
    [FILTERS.OWNED_BY]: 'ALL',
    [FILTERS.DASHBOARD_ID]: 'ALL',
    [FILTERS.IN_DASHBOARD]: undefined,
    [FILTERS.CUSTOM_REPORTS]: 'ALL',
    [FILTERS.FAVORITES]: undefined,
    [FILTERS.ASSIGNED]: 'ALL',
    [FILTERS.UPDATED_AT]: {
      startDate: null,
      endDate: null
    },
    [FILTERS.LAST_VIEWED_AT]: null,
    [FILTERS.BUSINESS_UNIT]: undefined,
    [FILTERS.UNUSED]: undefined,
    sortColumn: 'UPDATED_AT',
    sortDirection: 'descending'
  },
  filteredReportCount: undefined,
  templateResults: {
    limit: 15,
    templates: List(),
    searchResponse: undefined
  },
  aiTemplateResults: {
    templates: List(),
    searchResponse: undefined
  },
  restorableReports: {
    offset: undefined,
    limit: undefined,
    total: undefined,
    reports: List()
  }
});
export default handleActions({
  [GET_SAVED_REPORTS_REQUESTED](state) {
    return state.setIn(['status', 'getSavedReports'], PENDING).setIn(['status', 'normalizeReports'], PENDING);
  },
  [GET_SAVED_REPORTS_SUCCEEDED](state, {
    payload: reportsObj
  }) {
    return state.setIn(['status', 'getSavedReports'], SUCCEEDED).set('reports', reportsObj);
  },
  [GET_SAVED_REPORTS_FAILED](state) {
    return state.setIn(['status', 'getSavedReports'], FAILED);
  },
  [GET_REPORT_TEMPLATE_SEARCH_REQUESTED](state) {
    return state.setIn(['status', 'getReportTemplateSearchResults'], PENDING);
  },
  [GET_REPORT_TEMPLATE_SEARCH_SUCCEEDED](state, {
    payload: templatesList
  }) {
    return state.setIn(['status', 'getReportTemplateSearchResults'], SUCCEEDED).setIn(['templateResults', 'templates'], templatesList);
  },
  [GET_REPORT_TEMPLATE_SEARCH_FAILED](state) {
    return state.setIn(['status', 'getReportTemplateSearchResults'], FAILED);
  },
  [SET_TEMPLATES_LIMIT](state, {
    payload: templatesLimit
  }) {
    return state.setIn(['templateResults', 'limit'], templatesLimit);
  },
  [GET_ALL_RESTORABLE_REPORTS_REQUESTED](state) {
    return state.setIn(['status', 'getRestorableReports'], PENDING);
  },
  [GET_ALL_RESTORABLE_REPORTS_SUCCEEDED](state, {
    payload: restorableReportsObj
  }) {
    return state.setIn(['status', 'getRestorableReports'], SUCCEEDED).set('restorableReports', restorableReportsObj);
  },
  [GET_ALL_RESTORABLE_REPORTS_FAILED](state) {
    return state.setIn(['status', 'getRestorableReports'], FAILED);
  },
  [GET_REPORT_REQUESTED](state) {
    return state.setIn(['status', 'getReport'], PENDING).set('report', null);
  },
  [GET_REPORT_SUCCEEDED](state, {
    payload: report
  }) {
    return state.setIn(['status', 'getReport'], SUCCEEDED).set('report', report);
  },
  [GET_REPORT_FAILED](state) {
    return state.setIn(['status', 'getReport'], FAILED);
  },
  [CLEAR_REPORT](state) {
    return state.set('report', null);
  },
  [UPDATE_REPORT_REQUESTED](state) {
    return state.setIn(['status', 'updateCustomReport'], PENDING);
  },
  [UPDATE_REPORT_SUCCEEDED](state, {
    payload: report
  }) {
    return state.setIn(['status', 'updateCustomReport'], SUCCEEDED).setIn(['reports', 'reports'], replaceOrAddReport(state.getIn(['reports', 'reports']), report)).update('report', prevReport => {
      return !prevReport || prevReport.get('id') === report.get('id') ? report : prevReport;
    });
  },
  [UPDATE_REPORT_FAILED](state) {
    return state.setIn(['status', 'updateCustomReport'], FAILED);
  },
  [GET_REPORT_COUNTS_REQUESTED](state) {
    return state.setIn(['status', 'getReportCounts'], PENDING);
  },
  [GET_REPORT_COUNTS_SUCCEEDED](state, {
    payload: counts
  }) {
    return state.setIn(['status', 'getReportCounts'], SUCCEEDED).set('reportCounts', counts);
  },
  [GET_REPORT_COUNTS_FAILED](state) {
    return state.setIn(['status', 'getReportCounts'], FAILED);
  },
  [GET_FILTERED_REPORT_COUNTS_REQUESTED](state) {
    return state.setIn(['status', 'getFilteredReportCount'], PENDING);
  },
  [GET_FILTERED_REPORT_COUNTS_SUCCEEDED](state, {
    payload: count
  }) {
    return state.setIn(['status', 'getFilteredReportCount'], SUCCEEDED).set('filteredReportCount', count);
  },
  [GET_FILTERED_REPORT_COUNTS_FAILED](state) {
    return state.setIn(['status', 'getFilteredReportCount'], FAILED);
  },
  [SET_FILTERS](state, {
    payload: filters
  }) {
    return state.set('filters', filters);
  },
  [CLEAR_FILTERS](state) {
    return state.set('filters', initialState.get('filters'));
  },
  [GET_AI_SEARCH_REQUESTED](state) {
    return state.setIn(['status', 'getAiSearch'], PENDING);
  },
  [GET_AI_SEARCH_SUCCEEDED](state, {
    payload: response
  }) {
    return state.setIn(['status', 'getAiSearch'], SUCCEEDED).setIn(['aiTemplateResults', 'searchResponse'], response);
  },
  [GET_AI_SEARCH_FAILED](state) {
    return state.setIn(['status', 'getAiSearch'], FAILED);
  }
}, initialState);